import type { SyntheticEvent } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { normalizeTagName, tagKey } from '#common/tags';
import { useGetTagsQuery } from '#services/tag';

// Managed tags matching the input (case-insensitive), exact match first, minus
// those already picked. Appends the typed text as a create option when no tag
// matches it exactly; the server resolver creates it on save.
export function filterTagOptions(options: string[], input: string, selected: string[]) {
  const key = tagKey(input);
  const selectedKeys = new Set(selected.map(tagKey));
  const matches = options.filter((name) => !selectedKeys.has(tagKey(name)) && tagKey(name).includes(key));
  matches.sort((a, b) => Number(tagKey(b) === key) - Number(tagKey(a) === key));
  const known = options.some((name) => tagKey(name) === key) || selectedKeys.has(key);
  return key && !known ? [...matches, normalizeTagName(input)] : matches;
}

// Normalize and drop case-insensitive duplicates, keeping pick order.
export function dedupeTags(tags: string[]) {
  const seen = new Set<string>();
  return tags.map(normalizeTagName).filter((name) => {
    const key = tagKey(name);
    if (!name || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

interface TagPickerProps {
  name: string;
  value: string[];
  error?: boolean;
  onChange: (event: SyntheticEvent, change: { name: string; value: string[] }) => void;
}

export default function TagPicker({ name, value, error, onChange }: TagPickerProps) {
  const { data } = useGetTagsQuery(undefined);
  const options = ((data || []) as { name: string }[]).map((tag) => tag.name);
  const isKnown = (option: string) => options.some((tag) => tagKey(tag) === tagKey(option));

  return (
    <Autocomplete
      multiple
      freeSolo
      autoHighlight
      fullWidth
      size="small"
      sx={{ mb: 1 }}
      options={options}
      value={value}
      filterOptions={(opts, { inputValue }) => filterTagOptions(opts, inputValue, value)}
      renderOption={({ key, ...props }, option) => (
        <li key={key} {...props}>
          {isKnown(option) ? option : `Create "${option}"`}
        </li>
      )}
      onChange={(event, tags) => onChange(event, { name, value: dedupeTags(tags) })}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label="Tags"
          error={!!error}
          helperText="Pick an existing tag or type to create one."
        />
      )}
    />
  );
}
